import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { handleToggleRefresh, useApiContext } from '@trg/api';
import {
  useInfoAlertStore,
  useLayoutStore,
  useLoadingStore,
  useSnackbarStore,
} from '../../layout';
import { request } from '../utils';
import type {
  TrackingApiParams,
  TrackingApiResponse,
  TrackingNumApiResponse,
} from './tracking-api.type';

export const trackingApiPath = '/customerTracking/:op';
export const trackingNumApiPath = '/customerTracking/productTrackingNum';

export function useTrackingNumApi() {
  const { host } = useApiContext();
  if (host === undefined) throw new Error('ApiContext not found');

  return useQuery({
    queryKey: ['customerTracking', 'num'],
    queryFn: async () => {
      const { data } = await axios.get<TrackingNumApiResponse>(
        host + trackingNumApiPath
      );
      return data;
    },
  });
}

export interface TrackingApiOptions {
  onSuccess?: (response: TrackingApiResponse) => void;
  onError?: (error: { message: string }) => void;
}

export function useTrackingApi(
  action: 'add' | 'remove',
  options?: TrackingApiOptions
) {
  const showLoading = useLoadingStore.use.showLoading();
  const closeLoading = useLoadingStore.use.closeLoading();
  const openSnackbar = useSnackbarStore.use.openSnackbar();
  const openInfoAlert = useInfoAlertStore.use.openInfoAlert();
  const triggerFavoriting = useLayoutStore.use.triggerFavoriting();

  return useMutation({
    mutationKey: ['customerTracking', action],
    mutationFn: async (params: Pick<TrackingApiParams, 'sku'>) => {
      const response = await request<TrackingApiResponse>({
        method: 'post',
        url: `/customerTracking/${action}`,
        data: params,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        devHost: true,
        token: true,
        onStart: showLoading,
        onEnd: closeLoading,
      });
      return response;
    },
    ...handleToggleRefresh({
      onSuccess(response) {
        openSnackbar(action === 'add' ? '已成功加入收藏' : '已取消收藏');
        triggerFavoriting();
        options?.onSuccess?.(response);
      },
      onError: (error) => {
        openInfoAlert({ message: error.message });
        options?.onError?.(error);
      },
    }),
  });
}
